// Route (NO auth guard):  <Route path="/i/:token" element={<PublicInvoice />} />
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Loader2, Printer, Wallet } from 'lucide-react';
import styles from './PublicInvoice.module.css';

import {
  API_ORIGIN,
  buildInvoicePdf,
  buildUpiUrl,
  downloadBlob,
  formatINR,
  getInvoiceBrand,
  getPaperWidthPx,
  getPaymentInfo,
  printInvoiceNode,
  sanitizeFileName,
} from '../Invoiceexport';
import InvoiceDocument from '../InvoiceDocument/InvoiceDocument';

const A4_TEMPLATE = {
  id: 'a4-classic-themeClassic',
  name: 'Standard Professional',
  format: 'A4 Document',
  paperClass: 'paperA4',
  themeClass: 'themeClassic',
  isThermal: false,
};

const PublicInvoice = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const wrapRef = useRef(null);
  const docRef = useRef(null);
  const [wrapW, setWrapW] = useState(0);
  const [paperH, setPaperH] = useState(0);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_ORIGIN}/api/v1/public/invoices/${encodeURIComponent(token)}`);
        const json = await res.json().catch(() => ({}));
        if (!alive) return;
        if (!res.ok || !json.success) throw new Error(json.message || 'This invoice link is invalid or has expired.');
        setData(json.data);
        const b = getInvoiceBrand(json.data.settings || {}, json.data.invoice || {}, json.data.store);
        document.title = `${b.salonName} - Invoice ${json.data.invoice?.invoiceNo || ''}`;
      } catch (err) {
        if (alive) setError(err.message);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    if (!data || !wrapRef.current) return undefined;
    const ro = new ResizeObserver(([e]) => setWrapW(e.contentRect.width));
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [data]);

  useEffect(() => {
    if (!data || !docRef.current) return undefined;
    const el = docRef.current;
    setPaperH(el.offsetHeight);
    const ro = new ResizeObserver(() => setPaperH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [data]);

  if (error) {
    return (
      <div className={styles.stateScreen}>
        <h2>Invoice unavailable</h2>
        <p>{error}</p>
        <p className={styles.stateHint}>Please ask the salon to share the invoice again.</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.stateScreen}>
        <Loader2 size={28} className={styles.spin} />
        <p>Opening your invoice...</p>
      </div>
    );
  }

  const { invoice, settings = {}, store = null } = data;
  const brand = getInvoiceBrand(settings, invoice, store);
  const pay = getPaymentInfo(invoice);
  const invoiceNo = invoice.invoiceNo || '';
  const total = Number(invoice.summary?.grandTotal || 0);
  const payAmount = pay.balanceDue > 0.01 ? pay.balanceDue : invoice.status === 'draft' ? total : 0;
  const fileBase = sanitizeFileName(`${brand.salonName}-${invoiceNo}`);

  const paperPx = getPaperWidthPx(A4_TEMPLATE);
  const scale = wrapW ? Math.min(1, (wrapW - 24) / paperPx) : 1;
  const node = () => docRef.current?.firstElementChild;

  const download = async () => {
    setBusy('pdf');
    try {
      downloadBlob(await buildInvoicePdf(node(), A4_TEMPLATE), `${fileBase}.pdf`);
    } catch (e) {
      console.error(e);
    } finally {
      setBusy('');
    }
  };

  const print = async () => {
    setBusy('print');
    try {
      await printInvoiceNode(node(), A4_TEMPLATE, fileBase);
    } catch (e) {
      console.error(e);
    } finally {
      setBusy('');
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.topBar}>
        <div className={styles.topText}>
          <strong>{brand.salonName}</strong>
          <span>Invoice {invoiceNo} | {formatINR(total)}</span>
        </div>
        <div className={styles.topActions}>
          <button type="button" className={styles.ghostBtn} onClick={print} disabled={Boolean(busy)} aria-label="Print">
            {busy === 'print' ? <Loader2 size={15} className={styles.spin} /> : <Printer size={15} />}
            <span className={styles.hideSm}>Print</span>
          </button>
          <button type="button" className={styles.primaryBtn} onClick={download} disabled={Boolean(busy)}>
            {busy === 'pdf' ? <Loader2 size={15} className={styles.spin} /> : <Download size={15} />}
            Download PDF
          </button>
        </div>
      </header>

      <main className={styles.canvas} ref={wrapRef}>
        <div className={styles.frame} style={{ width: paperPx * scale, height: paperH ? paperH * scale : 'auto' }}>
          <div ref={docRef} className={styles.scaler} style={{ width: paperPx, transform: `scale(${scale})` }}>
            <InvoiceDocument invoice={invoice} settings={settings} store={store} template={A4_TEMPLATE} />
          </div>
        </div>
      </main>

      {brand.upiId && payAmount > 0 && (
        <a
          className={styles.payBar}
          href={buildUpiUrl({ upiId: brand.upiId, name: brand.salonName, amount: payAmount, note: `Invoice ${invoiceNo}` })}
        >
          <Wallet size={17} /> Pay {formatINR(payAmount)} via UPI
        </a>
      )}
    </div>
  );
};

export default PublicInvoice;